import React, { createContext, useCallback, useContext, useRef, useState, ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/common/Dialog';
import { Button } from '../../components/ui/button';

export interface ConfirmOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Style the confirm button as a destructive action (default true: confirmations here delete data) */
  destructive?: boolean;
}

/** Ask the user to confirm; resolves true on confirm, false on cancel/Esc/outside click */
export type ConfirmFn = (options: ConfirmOptions | string) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

/** Used when no ConfirmProvider is mounted (for example in isolated component tests) */
/* eslint-disable no-restricted-properties -- deliberate fallback when no provider is mounted */
const windowConfirm: ConfirmFn = options =>
  Promise.resolve(window.confirm(typeof options === 'string' ? options : options.message));
/* eslint-enable no-restricted-properties */

/**
 * Promise-based replacement for `window.confirm`, rendered as an accessible dialog
 * (focus trap, Esc to cancel).
 *
 * @example
 * const confirm = useConfirm();
 * if (!(await confirm('Delete this task?'))) return;
 */
export const useConfirm = (): ConfirmFn => useContext(ConfirmContext) ?? windowConfirm;

interface PendingConfirm {
  options: ConfirmOptions;
  resolve: (confirmed: boolean) => void;
}

export const ConfirmProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const pendingRef = useRef<PendingConfirm | null>(null);

  const settle = useCallback((confirmed: boolean) => {
    pendingRef.current?.resolve(confirmed);
    pendingRef.current = null;
    setPending(null);
  }, []);

  const confirm = useCallback<ConfirmFn>(
    options =>
      new Promise<boolean>(resolve => {
        // A second request while one is open cancels the first
        pendingRef.current?.resolve(false);
        const next = {
          options: typeof options === 'string' ? { message: options } : options,
          resolve,
        };
        pendingRef.current = next;
        setPending(next);
      }),
    []
  );

  const options = pending?.options;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog open={!!pending} onOpenChange={open => !open && settle(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{options?.title ?? 'Please confirm'}</DialogTitle>
            <DialogDescription>{options?.message}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => settle(false)}>
              {options?.cancelLabel ?? 'Cancel'}
            </Button>
            <Button
              variant={options?.destructive === false ? 'default' : 'destructive'}
              onClick={() => settle(true)}
            >
              {options?.confirmLabel ?? 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  );
};
